/**
 * @fileoverview Hook for expandable entry animations.
 * @module features/settings/components/integration-shared/useExpandableEntry
 */

import { useRef, useEffect } from 'react'
import { useSpring, useMotionValue, type MotionValue, type SpringOptions } from 'framer-motion'

interface UseExpandableEntryReturn {
  contentRef: React.RefObject<HTMLDivElement | null>
  animatedHeight: MotionValue<number>
}

const SPRING_CONFIG: SpringOptions = { stiffness: 300, damping: 30 }

/**
 * Hook for managing expandable entry animations.
 * @param isExpanded - Whether the entry is expanded
 * @param deps - Additional dependencies to trigger height recalculation
 */
export function useExpandableEntry(
  isExpanded: boolean,
  deps: unknown[] = []
): UseExpandableEntryReturn {
  const contentRef = useRef<HTMLDivElement>(null)
  const heightValue = useMotionValue(0)
  const animatedHeight = useSpring(heightValue, SPRING_CONFIG)

  useEffect(() => {
    if (contentRef.current) {
      heightValue.set(isExpanded ? contentRef.current.scrollHeight : 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, heightValue, ...deps])

  return { contentRef, animatedHeight }
}
